import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Admin() {
  const [refunds, setRefunds] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("refunds");

  const load = useCallback(async () => {
    const [r, o, u, s] = await Promise.all([
      api.get("/refunds"), api.get("/admin/orders"),
      api.get("/admin/users"), api.get("/admin/stats")
    ]);
    setRefunds(r.data); setOrders(o.data); setUsers(u.data); setStats(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (rid, decision) => {
    await api.post("/refunds/decision", { refund_id: rid, decision });
    toast.success(`Refund ${decision}`); load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="admin-page">
      <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold">Admin</div>
      <h1 className="font-display text-4xl font-black text-slate-900 mb-6">Operations control room</h1>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {Object.entries(stats).map(([k,v]) => (
            <div key={k} className="border border-slate-200 rounded p-4 bg-white">
              <div className="text-xs uppercase tracking-widest text-slate-500">{k.replace(/_/g, ' ')}</div>
              <div className="font-display text-2xl font-black text-slate-900">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {["refunds","orders","users"].map(t => (
          <Button key={t} variant={tab===t?"default":"outline"} onClick={()=>setTab(t)} data-testid={`admin-tab-${t}`}>{t}</Button>
        ))}
      </div>

      {tab === "refunds" && (
        <div className="border border-slate-200 rounded bg-white overflow-x-auto" data-testid="admin-refunds">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 text-left">
              <tr><th className="p-3">Refund</th><th>Order</th><th>Amount</th><th>Reason</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {refunds.map(r => (
                <tr key={r.refund_id} className="border-t border-slate-100">
                  <td className="p-3 font-mono text-xs">{r.refund_id}</td>
                  <td className="font-mono text-xs">{r.order_id}</td>
                  <td>{r.currency==="INR"?"₹":"$"}{r.amount}</td>
                  <td className="max-w-xs truncate">{r.reason}</td>
                  <td><Badge variant="outline">{r.status}</Badge></td>
                  <td className="p-3">
                    {r.status === "under_review" && (
                      <div className="flex gap-2">
                        <Button size="sm" className="btn-primary" onClick={()=>decide(r.refund_id,"approved")} data-testid={`approve-${r.refund_id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={()=>decide(r.refund_id,"rejected")} data-testid={`reject-${r.refund_id}`}>Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {refunds.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No refund requests</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "orders" && (
        <div className="border border-slate-200 rounded bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 text-left">
              <tr><th className="p-3">Order</th><th>Client</th><th>Service</th><th>Amount</th><th>Status</th></tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.order_id} className="border-t border-slate-100">
                  <td className="p-3 font-mono text-xs">{o.order_id}</td>
                  <td>{o.client_email}</td>
                  <td>{o.service_title}</td>
                  <td>{o.currency==="INR"?"₹":"$"}{o.amount}</td>
                  <td><Badge variant="outline">{o.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "users" && (
        <div className="border border-slate-200 rounded bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 text-left">
              <tr><th className="p-3">Email</th><th>Name</th><th>Role</th><th>Provider</th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} className="border-t border-slate-100">
                  <td className="p-3">{u.email}</td><td>{u.name}</td><td><Badge variant="outline">{u.role}</Badge></td><td>{u.auth_provider}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
